import { FeatureFlags } from '@domain/types';
import { isLoginWebview } from '@launcher/helpers/desktop';
import { Action } from '@reduxjs/toolkit';
import { tracking } from '@services/analytics';
import { legacyFeatureFlagsStore } from '@services/featureFlags/legacyFeatureFlags.store';
import { intercom } from '@services/intercom';
import { logger } from '@services/logger/logger.service';
import { monitoring } from '@services/monitoring';
import { realtimeClient } from '@services/realtimeClient.service';
import { UserData } from '@store/user/user.state';
import { EventChannel, eventChannel } from 'redux-saga';
import { all, fork, put, select, take, takeLatest } from 'redux-saga/effects';
import { RealTimeEvent } from '../../types/realtime';
import { fetchIdentitiesSuccess, InitFeatureFlags, initFeatureFlags } from '../user/user.actions';
import {
  getCurrentWorkspaceId,
  getIdentitiesReferences,
  getUser,
  selectLocale,
} from '../user/user.selector';

function getEventChannel(
  channelName: string,
): EventChannel<Action<string> & Record<string, unknown>> {
  return eventChannel((emitter) => {
    realtimeClient
      .getClient()
      .subscribe(channelName)
      .bind('pusher:subscription_error', (error: any) => {
        logger.error('LOGIC', `Pusher subscription error`, error);
      })
      .bind_global((event: string, payload: RealTimeEvent<unknown>) => {
        emitter({
          type: event,
          payload: {
            channel: channelName,
            ...payload,
          },
        });
      });

    return () => realtimeClient.getClient().unsubscribe(channelName);
  });
}

function* initTrackingService() {
  const user: UserData = yield select(getUser);
  const featureFlags = legacyFeatureFlagsStore.get();
  const locale: string = yield select(selectLocale);

  tracking.initUserData({
    userEmail: user.email,
    userFirstName: user.firstName,
    userId: user.uuid,
    userLastName: user.lastName,
    userWorkspaceId: user.currentWorkspaceId,
    currentApiDomain: user.currentApiDomain,
    locale,
  });
  tracking.initFeatureFlags(featureFlags);
  tracking.identify();
  tracking.trackClientSession(); // TODO: Move this to a userAuthenticateUser hook (when we have one)

  // Same chokepoint: the caller already excluded the desktop login webview, and the identity
  // verification JWT minted by Alphonse is on the user by the time we get here.
  intercom.boot({
    userId: user.uuid,
    userJwt: user.intercomUserJwt,
    locale,
  });
}

function* watchChannel(channel: EventChannel<Action<string> & Record<string, unknown>>) {
  while (true) {
    const action: Action<string> = yield take(channel);
    yield put(action);
  }
}

function* eventStreamSubscribe() {
  const identitiesReferences: string[] = yield select(getIdentitiesReferences);

  yield all(
    identitiesReferences.map((identityReference) =>
      fork(watchChannel, getEventChannel(identityReference.replace('/', '-'))),
    ),
  );
}

function* setMonitoringUser(featureFlags: FeatureFlags) {
  // TODO: Move this to userAuthenticateUser hook
  // How to update featureFlag properly (using tag?)
  const { email, uuid } = yield select(getUser);
  const workspaceId: string = yield select(getCurrentWorkspaceId);
  monitoring.setUser({
    email,
    id: uuid,
    featureFlags,
    workspaceId,
  });
}

function* onInitFeatureFlags(action: ReturnType<InitFeatureFlags>) {
  const updatedFeatureFlags = action.payload;

  yield setMonitoringUser(updatedFeatureFlags);

  // Dot not subscribe to pusher events or init tracking in the desktop webview
  if (!isLoginWebview()) {
    yield initTrackingService();
    yield eventStreamSubscribe();
  }
}

function* featureFlagsSagas() {
  // Wait for user identities
  yield all([take(fetchIdentitiesSuccess.type)]);

  // Init pusher channels only after feature flags init to avoid to have restarting pusher if "use-proxy" flag is ON
  yield all([takeLatest(initFeatureFlags.type, onInitFeatureFlags)]);
}

export { featureFlagsSagas };
